/**
 * Tells a live stream apart from a dead one — and, critically, keeps **startup** out of it.
 *
 * The steady-state rule is easy: bytes arrive constantly (the server sends a keepalive every ~2 s),
 * so six seconds of complete silence means the stream is gone and reconnecting is right.
 *
 * Startup is a different animal. Asking for `/video` makes the service dispatch `stream_start`, the
 * hub boot scrcpy on the phone and wait until both the control channel and the SPS geometry are up;
 * the response does not begin until that finishes. Measured against production: **4.7 s to the first
 * byte**, and more when the audio process starts alongside it.
 *
 * Policing that startup with the steady-state threshold is how the workspace hung on "Connecting…"
 * forever: the client aborted at exactly 6 s (nginx logged 499 with 0 bytes), and each retry booted a
 * fresh scrcpy, so the stream never got the chance to deliver its first frame. The old single-file
 * viewer had the same 6 s rule and survived only on a ~1.3 s accident — copying it would have carried
 * the bug forward.
 */

export const STARTUP_BUDGET_MS = 20000
export const STALL_BUDGET_MS = 6000

export class StreamWatchdog {
  private last = 0
  private streaming = false

  constructor(
    private readonly startupMs = STARTUP_BUDGET_MS,
    private readonly stallMs = STALL_BUDGET_MS,
  ) {}

  /** A request has just been sent (or re-sent): back to the generous startup budget. */
  begin(now: number): void {
    this.last = now
    this.streaming = false
  }

  /** Any bytes at all — a real frame or a keepalive. The first ones end startup. */
  bytes(now: number): void {
    this.last = now
    this.streaming = true
  }

  /** True when the stream has been silent longer than its current budget allows. */
  expired(now: number): boolean {
    const budget = this.streaming ? this.stallMs : this.startupMs
    return now - this.last > budget
  }

  get started(): boolean {
    return this.streaming
  }
}
